import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Briefcase, MapPin, Calendar, ExternalLink } from "lucide-react";

const MyApplications = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API}/my-applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplications(response.data);
    } catch (error) {
      console.error("Error loading applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      reviewed: "bg-blue-100 text-blue-700",
      shortlisted: "bg-purple-100 text-purple-700",
      accepted: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700"
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: "⏳",
      reviewed: "👀",
      shortlisted: "⭐",
      accepted: "✅",
      rejected: "❌"
    };
    return icons[status] || "📄";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
            <Briefcase className="w-10 h-10 text-indigo-500" />
            My Applications
          </h1>
          <p className="text-gray-600 text-lg">Track your job and internship applications</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-indigo-600">
                {applications.length}
              </p>
              <p className="text-sm text-gray-600">Total Applications</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-yellow-600">
                {applications.filter(a => a.status === "pending").length}
              </p>
              <p className="text-sm text-gray-600">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-blue-600">
                {applications.filter(a => a.status === "reviewed").length}
              </p>
              <p className="text-sm text-gray-600">Reviewed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-purple-600">
                {applications.filter(a => a.status === "shortlisted").length}
              </p>
              <p className="text-sm text-gray-600">Shortlisted</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-3xl font-bold text-green-600">
                {applications.filter(a => a.status === "accepted").length}
              </p>
              <p className="text-sm text-gray-600">Accepted</p>
            </CardContent>
          </Card>
        </div>

        {/* Applications List */}
        <div className="space-y-4">
          {applications.map((application) => (
            <Card key={application.id} className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold">
                        {application.opportunity?.title || "Unknown Position"}
                      </h3>
                      <Badge className={getStatusColor(application.status)}>
                        {getStatusIcon(application.status)} {application.status.toUpperCase()}
                      </Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" />
                        <span>{application.opportunity?.company_name || "Company"}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{application.opportunity?.location || "Location"}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>Applied: {new Date(application.applied_date).toLocaleDateString()}</span>
                      </div>
                      {application.opportunity?.type && (
                        <Badge variant="outline" className="capitalize">
                          {application.opportunity.type}
                        </Badge>
                      )}
                    </div>

                    {application.notes && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-sm font-semibold text-blue-900 mb-1">Admin Notes:</p>
                        <p className="text-sm text-blue-800">{application.notes}</p>
                      </div>
                    )}

                    {application.cover_letter && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <p className="text-sm font-semibold mb-1">Your Cover Letter:</p>
                        <p className="text-sm text-gray-700 line-clamp-2">{application.cover_letter}</p>
                      </div>
                    )}

                    {application.resume_link && (
                      <div className="mb-3">
                        <a 
                          href={application.resume_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View Resume
                        </a>
                      </div>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => navigate(`/opportunities/${application.opportunity_id}`)}
                  >
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {applications.length === 0 && (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-4">You haven't applied to any opportunities yet</p>
            <Button onClick={() => navigate("/opportunities")}>
              Browse Opportunities
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MyApplications;
